import type { ReactNode } from 'react';
import { memo, useContext, useMemo } from 'react';

import { Navigate, type To } from '@/js/libs/router';
import type { RolesInput } from '@/js/libs/use-auth';
import { AuthContext, matchRoles } from '@/js/libs/use-auth';

export interface Condition {
  roles: RolesInput;
  to: To;
}

interface RoleBasedRedirectorPropsBase {
  conditions: Condition[];
}

interface RoleBasedRedirectorPropsWithFallback extends RoleBasedRedirectorPropsBase {
  fallbackPath: To;
  noAccessElement?: never;
}

interface RoleBasedRedirectorPropsWithNoAccess extends RoleBasedRedirectorPropsBase {
  fallbackPath?: never;
  noAccessElement: ReactNode;
}

type RoleBasedRedirectorProps = RoleBasedRedirectorPropsWithFallback | RoleBasedRedirectorPropsWithNoAccess;

const RoleBasedRedirector = ({ conditions, noAccessElement, fallbackPath }: RoleBasedRedirectorProps) => {
  const { identityRoles } = useContext(AuthContext);

  const matchingCondition = useMemo<Condition | undefined>(
    () => conditions.find((condition) => matchRoles(condition.roles, identityRoles)),
    [conditions, identityRoles],
  );

  if (!matchingCondition) {
    if (fallbackPath) {
      return <Navigate to={fallbackPath} replace />;
    }

    return <>{noAccessElement}</>;
  }

  return <Navigate to={matchingCondition.to} replace />;
};

export default memo(RoleBasedRedirector);
